import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Ruler, Package, Shirt, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const HERO = "https://static.prod-images.emergentagent.com/jobs/00f7dcdb-2369-426b-9b75-6a937da6bf82/images/0a3f6a840f80279b19e284acfb0913815a46c249df6e0f141d595e851e261d52.png";

const collections = [
  { name: "Iconic KC", desc: "Restaurants, bars & landmarks", img: "https://static.prod-images.emergentagent.com/jobs/00f7dcdb-2369-426b-9b75-6a937da6bf82/images/afab61777a89c2ed9a4c4d7237eab94d196edd31bb64ee12861fd1f09c217215.png" },
  { name: "Tornado Alley", desc: "Midwest grit & weather", img: "https://static.prod-images.emergentagent.com/jobs/00f7dcdb-2369-426b-9b75-6a937da6bf82/images/9d350237084ef0731a4dcdb608de3853d6e6c5487e52bca331a479fead096a95.png" },
  { name: "Custom Designs", desc: "Made just for you", img: "https://images.pexels.com/photos/17143532/pexels-photo-17143532.jpeg" },
  { name: "Stickers & Home Goods", desc: "Deck out your space", img: "https://static.prod-images.emergentagent.com/jobs/00f7dcdb-2369-426b-9b75-6a937da6bf82/images/7a0533d37db5d6fea341c9d12e469d2b5bc1e05efde7de487f045c1edff1427e.png" },
];

const steps = [
  { icon: Ruler, title: "Pick Your Size", desc: "Choose your fit and size — S through 3XL, unisex, men's, or women's." },
  { icon: Package, title: "Get The Monthly Design", desc: "Each month a fresh KC-inspired tee ships straight to your door." },
  { icon: Shirt, title: "Wear KC History", desc: "Rep the restaurants, bars, and landmarks that built Kansas City." },
];

const Home = () => {
  const [featured, setFeatured] = useState(null);
  const [products, setProducts] = useState([]);
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get("/products").then((res) => {
      setProducts(res.data.slice(0, 4));
      setFeatured(res.data.find((p) => p.featured) || res.data[0]);
    });
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list, partner!");
      setEmail("");
    } catch {
      toast.error("Something went wrong.");
    }
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative grain overflow-hidden border-b-2 border-[#1C1C1C]">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-20" style={{ mixBlendMode: "multiply" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36 grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block bg-[#1C1C1C] text-[#FDFBF7] text-xs font-bold uppercase tracking-[0.25em] px-3 py-1.5 mb-6">
              Iconic KC Collection
            </span>
            <h1 className="font-display text-6xl sm:text-7xl md:text-8xl leading-[0.9] tracking-tight">
              Kansas City<br />Icons.<br /><span className="text-[#D35400]">Monthly.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#4A4A4A] max-w-md leading-relaxed">
              A monthly t-shirt subscription celebrating iconic KC restaurants, bars, landmarks, and local culture.
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link to="/tee-club" className="btn-primary px-8 py-4 inline-flex items-center gap-2" data-testid="hero-join-club">
                Join the Tee Club <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/shop" className="btn-ghost px-8 py-4 inline-flex items-center" data-testid="hero-shop-now">
                Shop Now
              </Link>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden md:block"
          >
            <div className="border-2 border-[#1C1C1C] hard-shadow-lg bg-[#F5F2EA] p-3 rotate-2">
              <img src={HERO} alt="Featured KC tee" className="w-full aspect-square object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED MONTHLY SHIRT */}
      {featured && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="border-2 border-[#1C1C1C] hard-shadow bg-[#F5F2EA] p-3 -rotate-1">
              <img src={featured.image} alt={featured.name} className="w-full aspect-square object-cover" />
            </div>
            <div>
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#D35400]">This Month's Drop</span>
              <h2 className="font-display text-5xl md:text-6xl mt-3 leading-none">{featured.name}</h2>
              <p className="mt-5 text-lg text-[#4A4A4A] leading-relaxed">{featured.description}</p>
              <div className="text-3xl font-bold text-[#C0392B] mt-5">${featured.price.toFixed(2)}</div>
              <div className="flex flex-wrap gap-4 mt-8">
                <Link to={`/product/${featured.id}`} className="btn-primary px-8 py-4 inline-flex items-center gap-2" data-testid="featured-view-product">
                  View Product <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/tee-club" className="btn-ghost px-8 py-4" data-testid="featured-subscribe">
                  Get It Monthly
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="bg-[#1C1C1C] text-[#FDFBF7] py-20 md:py-28 grain relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <h2 className="font-display text-5xl md:text-6xl text-center mb-4">How It Works</h2>
          <p className="text-center text-[#C9C4BA] mb-16 max-w-xl mx-auto">Three steps to wearing a piece of Kansas City every single month.</p>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                className="border-2 border-[#D35400] p-8 bg-[#242424]"
                data-testid={`how-step-${i + 1}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <s.icon className="w-10 h-10 text-[#D35400]" strokeWidth={2.2} />
                  <span className="font-display text-5xl text-[#3a3a3a]">0{i + 1}</span>
                </div>
                <h3 className="font-display text-3xl mb-2">{s.title}</h3>
                <p className="text-[#C9C4BA] leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <h2 className="font-display text-5xl md:text-6xl mb-12">Shop The Collections</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {collections.map((c) => (
            <Link
              key={c.name}
              to="/shop"
              data-testid={`collection-${c.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className="group relative border-2 border-[#1C1C1C] hard-shadow overflow-hidden bg-[#F5F2EA] hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-all"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img src={c.img} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-[#1C1C1C]/90 text-[#FDFBF7] p-4">
                <h3 className="font-display text-2xl leading-none">{c.name}</h3>
                <p className="text-xs text-[#C9C4BA] mt-1">{c.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-display text-5xl md:text-6xl">Fresh Off The Press</h2>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 font-display text-2xl uppercase hover:text-[#D35400]" data-testid="view-all-products">
            View All <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* EMAIL SIGNUP */}
      <section className="bg-[#D35400] text-white py-20 md:py-24 grain relative overflow-hidden">
        <div className="max-w-3xl mx-auto px-6 text-center relative">
          <h2 className="font-display text-5xl md:text-6xl mb-4">Join The Alley</h2>
          <p className="text-lg mb-8 text-white/90">Get early drops, KC stories, and member-only deals. No spam — just good Midwest mail.</p>
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              data-testid="newsletter-email"
              className="flex-1 px-5 py-4 text-[#1C1C1C] border-2 border-[#1C1C1C] focus:outline-none font-medium"
            />
            <button type="submit" className="bg-[#1C1C1C] text-white px-8 py-4 font-bold uppercase tracking-widest hover:bg-black transition-colors" data-testid="newsletter-submit">
              Sign Up
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Home;
